/** Presentation boundaries only; source timestamps/text remain authoritative for replay/history. */
export type DisplayTranscript = {
  id: string;
  role: string;
  text: string;
  firstReceivedTime: number;
  isFinal?: boolean;
  final?: boolean;
  sourceSegmentId?: string;
};

export type TypedTranscript = DisplayTranscript & {
  assistantSnapshot: Record<string, string>;
  pending: boolean;
};

export function captureTypedTranscript(
  text: string,
  voice: DisplayTranscript[],
  timestamp: number,
  id: string
): TypedTranscript {
  return {
    id,
    role: "user",
    text,
    firstReceivedTime: timestamp,
    pending: true,
    assistantSnapshot: Object.fromEntries(
      voice.filter((item) => item.role === "assistant").map((item) => [item.id, item.text])
    ),
  };
}

export function splitTypedTranscriptDisplay(
  voice: DisplayTranscript[],
  typed: TypedTranscript[]
): DisplayTranscript[] {
  const boundaries = [...typed].sort((a, b) => a.firstReceivedTime - b.firstReceivedTime);
  const rows: Array<DisplayTranscript & { order: number }> = boundaries.map((item, index) => ({
    ...item,
    order: index * 2 + 1,
  }));
  for (const source of voice) {
    if (source.role !== "assistant") {
      rows.push({ ...source, order: -1 });
      continue;
    }
    const cuts = boundaries
      .map((boundary, index) => ({ boundary, index }))
      .filter(({ boundary }) => boundary.firstReceivedTime >= source.firstReceivedTime);
    let offset = 0;
    let time = source.firstReceivedTime;
    let id = source.id;
    let order = -1;
    const fragments: Array<DisplayTranscript & { order: number }> = [];
    let rewritten = false;
    for (const { boundary, index } of cuts) {
      const prefix = boundary.assistantSnapshot[source.id] ?? "";
      if (!source.text.startsWith(prefix) || prefix.length < offset) {
        rewritten = true;
        break;
      }
      if (prefix.length > offset) {
        fragments.push({
          ...source,
          id,
          text: source.text.slice(offset, prefix.length),
          firstReceivedTime: time,
          sourceSegmentId: source.id,
          order,
        });
      }
      offset = prefix.length;
      time = boundary.firstReceivedTime;
      id = `${source.id}:after:${boundary.id}`;
      order = index * 2 + 2;
    }
    if (rewritten) {
      // A revised prefix is not a known split point. Show the corrected source
      // intact after the latest relevant input; never duplicate or drop words.
      const last = cuts[cuts.length - 1];
      rows.push({
        ...source,
        sourceSegmentId: source.id,
        firstReceivedTime: last.boundary.firstReceivedTime,
        order: last.index * 2 + 2,
      });
      continue;
    }
    if (offset < source.text.length) {
      fragments.push({
        ...source,
        id,
        text: source.text.slice(offset),
        firstReceivedTime: time,
        sourceSegmentId: source.id,
        order,
      });
    }
    rows.push(...fragments.filter((fragment) => fragment.text.trim()));
  }
  return rows
    .sort((a, b) => a.firstReceivedTime - b.firstReceivedTime || a.order - b.order)
    .map(({ order: _order, ...row }) => row);
}

export function transcriptReplaySource(
  sources: DisplayTranscript[],
  display: DisplayTranscript[],
  index: number
) {
  const row = display[index];
  const sourceId = row.sourceSegmentId ?? row.id;
  const sourceIndex = sources.findIndex((source) => source.id === sourceId);
  const text = sources[sourceIndex]?.text ?? "";
  return {
    sourceIndex,
    text,
    wholeResponse: text !== row.text,
    isLastFragment: !display
      .slice(index + 1)
      .some((item) => (item.sourceSegmentId ?? item.id) === sourceId),
  };
}
